/**
 * Circuit Breaker
 *
 * Prevents cascading failures by temporarily stopping requests to failing services.
 *
 * States:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Failures exceeded threshold, requests blocked
 * - HALF_OPEN: Testing if service recovered
 */

import { ENV_CONFIG } from "@/config";
import { circuitLog } from "./logger";

// =============================================================================
// TYPES
// =============================================================================

export type CircuitState = "CLOSED" | "OPEN" | "HALF_OPEN";

interface CircuitConfig {
  /** Number of failures before opening circuit */
  failureThreshold: number;
  /** Time in ms to wait before trying again (half-open) */
  resetTimeout: number;
  /** Number of successful requests in half-open to close circuit */
  successThreshold: number;
  /** Time window in ms to count failures */
  failureWindow: number;
}

interface CircuitStatus {
  state: CircuitState;
  failures: number;
  successes: number;
  lastFailureTime: number | null;
  lastStateChange: number;
  failureTimestamps: number[];
}

// =============================================================================
// DEFAULT CONFIG
// =============================================================================

const DEFAULT_CONFIG: CircuitConfig = {
  failureThreshold: 5, // Open after 5 failures
  resetTimeout: 30000, // Try again after 30 seconds
  successThreshold: 2, // Close after 2 successes in half-open
  failureWindow: 60000, // Count failures in last 60 seconds
};

// =============================================================================
// STATE
// =============================================================================

/** Circuit breakers by key */
const circuits = new Map<string, CircuitStatus>();

/** Configurations by key (allows per-service config) */
const configs = new Map<string, CircuitConfig>();

/** Listeners for state changes */
const stateListeners = new Set<
  (key: string, state: CircuitState, prevState: CircuitState) => void
>();

// =============================================================================
// CORE FUNCTIONS
// =============================================================================

/**
 * Get or create circuit status for a key.
 */
function getOrCreateCircuit(key: string): CircuitStatus {
  let circuit = circuits.get(key);

  if (!circuit) {
    circuit = {
      state: "CLOSED",
      failures: 0,
      successes: 0,
      lastFailureTime: null,
      lastStateChange: Date.now(),
      failureTimestamps: [],
    };
    circuits.set(key, circuit);
  }

  return circuit;
}

/**
 * Get configuration for a circuit.
 */
function getConfig(key: string): CircuitConfig {
  return configs.get(key) ?? DEFAULT_CONFIG;
}

/**
 * Count failures within the time window.
 */
function countRecentFailures(
  circuit: CircuitStatus,
  config: CircuitConfig
): number {
  const now = Date.now();
  const windowStart = now - config.failureWindow;

  // Clean up old timestamps
  circuit.failureTimestamps = circuit.failureTimestamps.filter(
    (ts) => ts > windowStart
  );

  return circuit.failureTimestamps.length;
}

/**
 * Transition circuit to a new state.
 */
function transitionState(
  key: string,
  circuit: CircuitStatus,
  newState: CircuitState
): void {
  const prevState = circuit.state;

  if (prevState === newState) return;

  circuit.state = newState;
  circuit.lastStateChange = Date.now();

  // Reset counters on state change
  if (newState === "HALF_OPEN") {
    circuit.successes = 0;
  } else if (newState === "CLOSED") {
    circuit.failures = 0;
    circuit.successes = 0;
    circuit.failureTimestamps = [];
  }

  circuitLog.info(`${key}: ${prevState} → ${newState}`);

  // Notify listeners
  stateListeners.forEach((listener) => listener(key, newState, prevState));
}

/**
 * Record a successful request.
 */
export function recordSuccess(key: string): void {
  const circuit = getOrCreateCircuit(key);
  const config = getConfig(key);

  if (circuit.state === "HALF_OPEN") {
    circuit.successes++;

    if (circuit.successes >= config.successThreshold) {
      transitionState(key, circuit, "CLOSED");
    }
  }
}

/**
 * Record a failed request.
 */
export function recordFailure(key: string): void {
  const circuit = getOrCreateCircuit(key);
  const config = getConfig(key);

  circuit.failures++;
  circuit.lastFailureTime = Date.now();
  circuit.failureTimestamps.push(Date.now());

  if (circuit.state === "HALF_OPEN") {
    // Any failure in half-open reopens the circuit
    transitionState(key, circuit, "OPEN");
  } else if (circuit.state === "CLOSED") {
    const recentFailures = countRecentFailures(circuit, config);

    if (recentFailures >= config.failureThreshold) {
      transitionState(key, circuit, "OPEN");
    }
  }
}

/**
 * Check if circuit should transition from OPEN to HALF_OPEN.
 */
function checkReset(key: string): void {
  const circuit = getOrCreateCircuit(key);
  const config = getConfig(key);

  if (circuit.state === "OPEN") {
    const timeSinceStateChange = Date.now() - circuit.lastStateChange;

    if (timeSinceStateChange >= config.resetTimeout) {
      transitionState(key, circuit, "HALF_OPEN");
    }
  }
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Configure a circuit breaker.
 * Call before using the circuit.
 */
export function configureCircuit(
  key: string,
  config: Partial<CircuitConfig>
): void {
  configs.set(key, { ...DEFAULT_CONFIG, ...config });
}

/**
 * Get the current state of a circuit.
 */
export function getCircuitState(key: string): CircuitState {
  checkReset(key);
  return getOrCreateCircuit(key).state;
}

/**
 * Get detailed circuit status.
 */
export function getCircuitStatus(
  key: string
): CircuitStatus & { config: CircuitConfig } {
  checkReset(key);
  return {
    ...getOrCreateCircuit(key),
    config: getConfig(key),
  };
}

/**
 * Check if a circuit allows requests.
 */
export function isCircuitClosed(key: string): boolean {
  const state = getCircuitState(key);
  return state === "CLOSED" || state === "HALF_OPEN";
}

/**
 * Execute a function with circuit breaker protection.
 */
export async function withCircuitBreaker<T>(
  key: string,
  fn: () => Promise<T>,
  fallback?: T | (() => T)
): Promise<T> {
  checkReset(key);

  const circuit = getOrCreateCircuit(key);

  // Check if circuit is open
  if (circuit.state === "OPEN") {
    circuitLog.debug(`${key}: Request blocked (circuit OPEN)`);

    if (fallback !== undefined) {
      return typeof fallback === "function"
        ? (fallback as () => T)()
        : fallback;
    }

    throw new CircuitOpenError(key);
  }

  try {
    const result = await fn();
    recordSuccess(key);
    return result;
  } catch (error) {
    recordFailure(key);
    throw error;
  }
}

/**
 * Manually reset a circuit to CLOSED state.
 */
export function resetCircuit(key: string): void {
  const circuit = getOrCreateCircuit(key);
  transitionState(key, circuit, "CLOSED");
  circuit.failureTimestamps = [];
  circuitLog.info(`${key}: Manually reset`);
}

/**
 * Subscribe to circuit state changes.
 */
export function onCircuitStateChange(
  listener: (key: string, state: CircuitState, prevState: CircuitState) => void
): () => void {
  stateListeners.add(listener);
  return () => stateListeners.delete(listener);
}

// =============================================================================
// ERROR
// =============================================================================

/**
 * Error thrown when circuit is open and no fallback provided.
 */
export class CircuitOpenError extends Error {
  constructor(public readonly circuitKey: string) {
    super(`Circuit "${circuitKey}" is OPEN - request blocked`);
    this.name = "CircuitOpenError";
  }
}

// =============================================================================
// PRESET CONFIGURATIONS
// =============================================================================

export const CIRCUIT_PRESETS = {
  /** For API calls - standard settings */
  api: {
    failureThreshold: 5,
    resetTimeout: 30000,
    successThreshold: 2,
    failureWindow: 60000,
  },
  /** For critical operations - more lenient */
  critical: {
    failureThreshold: 10,
    resetTimeout: 60000,
    successThreshold: 3,
    failureWindow: 120000,
  },
} as const;

// =============================================================================
// DEBUG (development only)
// =============================================================================

if (ENV_CONFIG.isDev) {
  window.__circuitBreaker = {
    getState: getCircuitState,
    getStatus: getCircuitStatus,
    reset: resetCircuit,
    configure: configureCircuit,
    presets: CIRCUIT_PRESETS,
  };
}
